<!--
  LOGIN VIEW FOR © AREA 19, OCG WEB APP.
  ALLOWS USERS TO LOG IN TO THEIR ACCOUNT,
  BY ACCESSING A DATABASE AND VALIDATING EMAIL AND PASSWORD.
  DEVELOPED IN MONTREAL, QC, CANADA.
  JULY 2023.
 -->

<script setup lang="ts">
import AppLogoComponent from '@src/components/AppLogoComponent.vue';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useAlert } from '../composables/useAlert';
import { useAppState } from '../store/ocg-app-state';
import { supabase } from '../supabase';
import SingleForm from './SingleForm.vue';

const props = defineProps({
  redirect: {
    type: String,
    default: '/home',
  },
});

const alert = useAlert();
const router = useRouter();
const appState = useAppState();

const loginFailed = ref<string | null>(null);
const busy = ref(false);
const recoveryMode = ref(false);
const email = ref('');
const password = ref('');
const otc = ref('');
const showPassword = ref(false);

const login = async (event: Event) => {
  event.preventDefault();
  try {
    busy.value = true;
    if (!recoveryMode.value) {
      if (!email.value || !password.value) {
        loginFailed.value = 'Please enter your email and password';
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({
        email: email.value,
        password: password.value,
      });
      if (error) throw error;
    }
    else {
      if (!email.value || !otc.value) {
        loginFailed.value = 'Please enter your email and one-time code';
        return;
      }
      recoveryMode.value = false;
      const { error } = await supabase.auth.verifyOtp({
        email: email.value,
        token: otc.value,
        type: 'recovery',
      });
      if (error) throw error;
    }
  }
  catch (error) {
    if (error instanceof Error) {
      if (error.message === 'Invalid login credentials')
        loginFailed.value = 'Email not found or invalid password';
      else if (error.message === 'Token has expired or is invalid')
        loginFailed.value = 'One-time code is invalid or has expired';
      else alert.error(`An error occurred while logging in: ${error.message}`);
    }
  }
  finally {
    busy.value = false;
  }
};

const resetPassword = async () => {
  loginFailed.value = null;
  try {
    busy.value = true;
    if (!email.value) {
      loginFailed.value = 'Please enter your email';
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email.value);
    if (error) throw error;
    recoveryMode.value = true;
    alert.success('Please check your email for the password reset link!');
  }
  catch (error) {
    if (error instanceof Error) {
      alert.error(
        `An error occurred while resetting your password: ${error.message}`,
      );
    }
  }
  finally {
    busy.value = false;
  }
};

const handleFocus = () => {
  loginFailed.value = null;
};

watch(
  () => !!appState.session,
  (newValue) => {
    if (newValue) {
      router.push({ path: props.redirect, force: false });
    }
  },
  { immediate: true },
);
</script>

<template>
  <!-- The main container with a dynamic class binding -->
  <SingleForm>
    <div class="flex justify-center mb-10">
      <AppLogoComponent />
    </div>
    <h1 class="text-center mt-6">
      OCG19
    </h1>
    <!-- Title for the login section -->
    <h4 class="text-center">
      Sign in to your account
    </h4>

    <!-- Login form -->
    <form @submit="login">
      <!-- Label and input field for the email -->
      <label for="email">
        <h4 class="text-left mt-6">Email</h4>
        <input
          id="email"
          v-model="email"
          class="general-input w-full"
          type="text"
          required
          placeholder="Enter your email"
          @focusin="handleFocus"
        ></label>

      <!-- Label and input field for the password -->
      <label v-if="!recoveryMode" for="password"><h4 class="text-left mt-6">Password</h4>
        <div class="relative">
          <input
            id="password"
            v-model="password"
            class="general-input w-full"
            :type="showPassword ? 'text' : 'password'"
            placeholder="Enter your password"
            :required="!recoveryMode"
            @focusin="handleFocus"
          >
          <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
          <div class="eye-icon" @mousedown="showPassword = !showPassword">
            <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
          </div>
        </div>
      </label>
      <label v-else for="otc">One-time email code
        <input
          id="otc"
          v-model="otc"
          class="general-input"
          type="text"
          size="6"
          :required="recoveryMode"
          @focusin="handleFocus"
        ></label>
      <!-- Invalid message that appears if credentials are not found. -->
      <div :style="{ opacity: !!loginFailed ? 1 : 0 }">
        <h6 :style="{ color: 'var(--md-warning)' }">
          {{ loginFailed }}
        </h6>
      </div>

      <!-- Submit button to initiate the login process -->
      <div class="d-flex flex-column flex-wrap mt-6">
        <button
          type="submit"
          class="self-center button btn-secondary min-w-30"
          :disabled="busy"
        >
          {{ busy ? '...' : recoveryMode ? 'Reset Password' : 'Sign In' }}
        </button>

        <button
          v-show="!recoveryMode"
          class="button btn-outline-simple"
          type="button"
          style="align-self: center"
          :disabled="busy"
          @click="resetPassword"
        >
          Reset Password
        </button>
      </div>
    </form>
  </SingleForm>
</template>

<style scoped>
.eye-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  transition: opacity 0.3s ease;
  color: var(--md-on-surface);
}

.eye-icon:hover {
  opacity: 0.7;
}
</style>
